<template>
  <footer class="footer">
    <div class="copyright">© NEXON Korea Corporation All Rights Reserved.</div>
    <div class="language-selector-container">
      <div id="language-selector" class="selected-language" :class="{ expand: expanded }" @click="toggle">
        <span class="language-icon">
          <i-local-globe />
        </span>
        <span class="language-text">
          {{ $t(`languages.${currentLocale}`) }}
        </span>
        <span class="arrow-icon">
          <i-local-btn-downarrow />
        </span>
      </div>

      <Popover
        custom-class="language-option-area"
        placement="top"
        target="language-selector"
        triggers="click"
        :show="expanded"
      >
        <div class="popover-body">
          <div
            v-for="locale in languages"
            :key="locale"
            class="language-option"
            :class="{ selected: locale === currentLocale }"
            @click="setLang(locale)"
          >
            {{ $t(`languages.${locale}`) }}
          </div>
        </div>
      </Popover>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { SUPPORT_LOCALES } from '@/i18n'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { locale: currentLocale } = useI18n()

const route = useRoute()
const router = useRouter()
const languages = SUPPORT_LOCALES

const expanded = ref(false)

function toggle() {
  expanded.value = !expanded.value
}
function setLang(locale: string) {
  localStorage.setItem('ial/locale', locale) // store locale only when it's manual setting
  router.replace({ params: { locale, from: location.href }, query: route.query })
  expanded.value = false
}
</script>

<style lang="less" scoped>
.footer {
  @media (max-width: 460px) {
    width: 360px;
    max-width: 360px;
    max-width: 360px;

    padding-left: 20px;
    padding-right: 20px;
    flex-direction: column-reverse;
    align-items: center;
    gap: 8px;
  }

  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  gap: 15px;

  width: 100%;
  position: fixed;
  bottom: 0;
}

.copyright {
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.25999999046325684px;
  text-align: center;
  color: var(--footer-copyright);
}

.selected-language,
.language-icon,
.arrow-icon {
  display: flex;
  align-items: center;
}

.language-selector-container {
  :hover {
    cursor: pointer;
  }
}

.selected-language {
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.3px;
  color: var(--footer-lang-selected-text);

  &:deep {
    .language-icon {
      width: 15px;
      height: 15px;
      margin-right: 5px;

      @media (prefers-color-scheme: dark) {
        path {
          stroke: var(--footer-lang-text);
        }
      }
    }
    .arrow-icon {
      width: 13px;
      height: 13px;
      margin-left: 7px;
      transform: rotate(180deg);
    }
  }

  &.expand {
    .arrow-icon svg {
      transform: rotate(180deg);
    }
  }

  .language-text {
    color: var(--footer-lang-text);
  }
}
</style>

<style lang="less">
.language-option-area {
  box-sizing: border-box;
  text-align: center;
  border: 1px solid var(--footer-lang-option-area-border);
  border-radius: 5px;
  background-color: var(--footer-lang-selector-bg);
  max-height: 130px;
  padding: 5px;
}

.popover-body {
  max-height: 120px;
  overflow: auto;
  padding: 0;
  padding-right: 4px;

  .language-option {
    box-sizing: border-box;
    padding: 0.5rem 0.625rem; // 8px 10px
    cursor: pointer;
    height: 35px;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: #8f96a3;

    &:hover,
    &:focus {
      background-color: var(--footer-lang-highlight-bg);
      color: var(--footer-lang-highlight-text);
    }
    &.selected {
      font-weight: 700;
      color: var(--footer-lang-highlight-text);
    }
  }

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 0.25rem; //4px;
    height: 60px;
  }
  /* Track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: var(--footer-lang-scroller-thunb-bg);
    border-radius: 2px;
    width: 0.25rem; //4px;
    height: 60px;
  }
}
</style>
